/**
 * Sports Director — boot.
 *
 * Full-window WebGL canvas behind the DOM (console + screens). Title/menus run an
 * attract-mode session (muted sim + bot director, PROGRAM feed only, dimmed);
 * starting a match swaps it for a full play session.
 *
 * URL params (testing):
 *   ?autoplay=bot&skill=0.9&speed=4&half=60&seed=42  skip menus, bot directs a quick match,
 *                                                    auto-continue at half time, ends on the report card
 *     &difficulty=rookie|pro|legend                  (autoplay difficulty, default pro)
 *   ?screen=quick|settings|howto|...                  open a screen directly
 *   ?quality=low|med|high                             override the saved quality setting
 *   ?attract=0                                        disable the attract-mode backdrop
 *   ?training=1                                       jump straight into THE FIRST BROADCAST
 *   ?weather=clear|overcast|rain|snow|fog             force the weather of every generated match
 *   ?time=afternoon|golden_hour|night                 force the time of day of every generated match
 */
import './ui/styles.css';
import './game/game.css';
import * as THREE from 'three';
import type { MatchConfig, ResultSummary, TimeOfDay, Weather } from './core/contracts';
import { generateMatchConfig } from './sim/teams';
import { createScreens, type ScreenName } from './ui/screens';
import type { MatchExtras, StartMatchOptions } from './meta/progress';
import { sanitizeSettings, type Settings } from './meta/settings';
import { createAudio } from './audio';
import { createMatchSession, pixelRatioFor, type MatchSession, type SessionStats } from './game/session';
import type { HighlightsData } from './highlights/recorder';
import { canExportVideo, createHighlightsReel, type HighlightsReel } from './highlights/reel';
import { createTraining, type Training } from './tutorial';
import { createCoach, type Coach } from './tutorial/coach';

const qs = new URLSearchParams(location.search);
const num = (k: string, d: number): number => {
  const v = Number(qs.get(k));
  return qs.has(k) && Number.isFinite(v) ? v : d;
};
const autoplay = qs.get('autoplay') === 'bot';
const auto = {
  skill: Math.max(0, Math.min(1, num('skill', 0.9))),
  speed: Math.max(0.25, Math.min(16, num('speed', 1))),
  half: Math.max(20, num('half', 120)),
  seed: Math.floor(num('seed', 42)),
  difficulty: (['rookie', 'pro', 'legend'] as const).find((d) => d === qs.get('difficulty')) ?? 'pro',
};
const attractEnabled = qs.get('attract') !== '0';
const WEATHERS: readonly Weather[] = ['clear', 'overcast', 'rain', 'snow', 'fog'];
const TIMES: readonly TimeOfDay[] = ['afternoon', 'golden_hour', 'night'];
/** Testing overrides for the look of a match (?weather=rain&time=night). */
const lookOverrides: { weather?: Weather; timeOfDay?: TimeOfDay } = {};
if (WEATHERS.includes(qs.get('weather') as Weather)) lookOverrides.weather = qs.get('weather') as Weather;
if (TIMES.includes(qs.get('time') as TimeOfDay)) lookOverrides.timeOfDay = qs.get('time') as TimeOfDay;

// ------------------------------------------------------------------ renderer
const canvas = document.createElement('canvas');
canvas.id = 'sd-canvas';
document.body.prepend(canvas);
const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', alpha: false });
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.setClearColor(0x050607, 1);
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
renderer.setSize(window.innerWidth, window.innerHeight);

// ------------------------------------------------------------------ DOM roots
const app = document.getElementById('app')!;
const consoleRoot = document.createElement('div');
consoleRoot.className = 'sd-console-root';
app.append(consoleRoot);

const audio = createAudio();

let session: MatchSession | null = null;
let training: Training | null = null;
let coach: Coach | null = null;
let attract: MatchSession | null = null;
let lastStats: SessionStats | null = null;
let lastResult: { summary: ResultSummary; extras: MatchExtras } | null = null;
let lastHighlights: HighlightsData | null = null;
let reel: HighlightsReel | null = null;
let lastEventCount: number | null = null;
let lastVideo: { size: number; type: string; seconds: number; filename: string } | null = null;
const errors: string[] = [];
window.addEventListener('error', (e) => errors.push(String(e.message)));
window.addEventListener('unhandledrejection', (e) => errors.push(String((e as PromiseRejectionEvent).reason)));

// ------------------------------------------------------------------ screens
const screens = createScreens(app, {
  generateMatchConfig: (seed) => generateMatchConfig(seed, lookOverrides),
  onStartMatch: (config, options) => startMatch(config, options),
  onResume: () => { session?.setPaused(false); },
  onQuit: () => { endSession(); startAttract(); },
  onSecondHalf: () => { session?.resumeSecondHalf(); },
  onSettingsChanged: (s) => applySettings(s),
  onSkipTraining: () => skipTraining(),
  shareUrl: 'https://sportsdirector.sjdf.co.uk',
  version: '0.1',
  highlights: {
    available: () => !!lastHighlights?.clips.length,
    canExport: () => canExportVideo(),
    watch: () => openReel(false),
    save: () => { if (reel) reel.startExport(); else openReel(true); },
    isOpen: () => !!reel,
  },
});

function currentSettings(): Settings {
  const s = sanitizeSettings(screens.store.get().settings);
  const q = qs.get('quality');
  if (q === 'low' || q === 'med' || q === 'high') s.quality = q;
  return s;
}

function applySettings(s0: Settings): void {
  const s = { ...s0 };
  const q = qs.get('quality');
  if (q === 'low' || q === 'med' || q === 'high') s.quality = q;
  audio.setMaster(s.masterVolume);
  session?.applySettings(s);
  attract?.applySettings(s);
}

// ------------------------------------------------------------------ attract mode
let attractSeed = (Date.now() >>> 0) % 100000;
function startAttract(): void {
  if (!attractEnabled || autoplay || attract || session || reel) return;
  const config = generateMatchConfig(attractSeed++, { ...lookOverrides, halfLengthSeconds: 300 });
  attract = createMatchSession({
    mode: 'attract', config, renderer, settings: currentSettings(),
    onFullTime: () => { stopAttract(); startAttract(); },
  });
  document.documentElement.classList.add('sd-attract-on');
  attract.start();
  audio.setPaused(true);
}
function stopAttract(): void {
  if (!attract) return;
  attract.dispose();
  attract = null;
  document.documentElement.classList.remove('sd-attract-on');
}

// ------------------------------------------------------------------ play session
function endSession(): void {
  training?.dispose(); training = null;
  coach?.dispose(); coach = null;
  if (!session) return;
  lastStats = session.stats();
  session.dispose();
  session = null;
  audio.setPaused(true);
}

function startMatch(config: MatchConfig, options: StartMatchOptions): void {
  closeReel();
  stopAttract();
  endSession();
  void audio.resume().catch(() => undefined);
  const isTraining = options.mode === 'tutorial';
  // Training teaches hot-punch keys, so it always runs in hot-punch mode.
  const settings = isTraining ? { ...currentSettings(), punchMode: 'hot' as const } : currentSettings();
  session = createMatchSession({
    mode: 'play', config, renderer, settings, uiRoot: consoleRoot, audio,
    start: options,
    bot: autoplay ? { skill: auto.skill } : null,
    timeScale: autoplay ? auto.speed : 1,
    inputEnabled: () => !screens.isOpen(),
    booth: isTraining ? 'scripted' : 'auto',
    onPauseRequest: () => {
      if (!session || screens.isOpen()) return;
      session.setPaused(true);
      if (training) screens.show('training_skip');
      else screens.showPause();
    },
    onHalfTime: (p) => {
      screens.showHalfTime(p);
      if (autoplay) setTimeout(() => { if (screens.current() === 'halftime') { screens.hide(); session?.resumeSecondHalf(); } }, 1500);
    },
    onFullTime: (summary, extras) => {
      lastResult = { summary, extras };
      lastHighlights = session?.highlights() ?? null;
      lastEventCount = session?.events().length ?? null;
      endSession();
      screens.showReport(summary, extras);
      startAttract();
    },
  });
  session.start();
  if (isTraining) {
    training = createTraining({
      session, config, root: app, audio,
      blocked: () => screens.isOpen(),
      onSkipRequest: () => { if (session && !screens.isOpen()) { session.setPaused(true); screens.show('training_skip'); } },
      onComplete: (r) => {
        screens.store.update((d) => { d.training = { done: true, skipped: false, letter: r.report.letter }; });
        lastTraining = { step: 'complete', learned: r.learned, letter: r.report.letter, rating: r.report.rating };
        endSession();
        screens.show('training_report', r);
        startAttract();
      },
    });
  } else if (options.difficulty === 'rookie' && !autoplay) {
    coach = createCoach({ session, root: app, enabled: () => currentSettings().coaching, blocked: () => screens.isOpen() });
  }
}

function skipTraining(): void {
  if (!screens.store.get().training.done) screens.store.update((d) => { d.training.skipped = true; });
  lastTraining = { step: 'skipped', learned: [], letter: null, rating: null };
  endSession();
  startAttract();
}
let lastTraining: { step: string; learned: string[]; letter: string | null; rating: number | null } | null = null;

// ------------------------------------------------------------------ highlights reel
function openReel(exportNow: boolean): void {
  if (reel || session || !lastHighlights?.clips.length || !lastResult) return;
  stopAttract();
  void audio.resume().catch(() => undefined);
  document.documentElement.classList.add('sd-reel-on');
  const q = currentSettings().quality;
  reel = createHighlightsReel({
    renderer, data: lastHighlights, summary: lastResult.summary, root: app, audio,
    quality: q === 'med' ? 'medium' : q, pixelRatio: pixelRatioFor(q, 'play'), exportOnStart: exportNow,
    onClose: closeReel,
    onExported: (blob, info) => { lastVideo = { size: blob.size, type: blob.type, seconds: Math.round(info.seconds * 10) / 10, filename: info.filename }; },
  });
}
function closeReel(): void {
  if (!reel) return;
  reel.dispose();
  reel = null;
  document.documentElement.classList.remove('sd-reel-on');
  startAttract();
}

// ------------------------------------------------------------------ window plumbing
function onResize(): void {
  renderer.setSize(window.innerWidth, window.innerHeight);
  session?.invalidate();
  attract?.invalidate();
  reel?.resize();
}
window.addEventListener('resize', onResize);

const unlockAudio = () => {
  void audio.resume().then(() => {
    audio.setMaster(currentSettings().masterVolume);
    audio.setPaused(!session || session.paused);
  }).catch(() => undefined);
  window.removeEventListener('pointerdown', unlockAudio, true);
  window.removeEventListener('keydown', unlockAudio, true);
};
window.addEventListener('pointerdown', unlockAudio, true);
window.addEventListener('keydown', unlockAudio, true);

document.addEventListener('visibilitychange', () => {
  if (document.hidden && session && !session.paused && !screens.isOpen() && !autoplay) {
    session.setPaused(true);
    screens.showPause();
  }
});

// Debug / test surface.
declare global {
  interface Window { __sd: unknown }
}
window.__sd = {
  get stats() { return session?.stats() ?? attract?.stats() ?? lastStats; },
  get result() { return lastResult; },
  /** Sim event count of the last finished match (determinism checks). */
  get eventCount() { return lastEventCount; },
  get screen() { return screens.current(); },
  get errors() { return errors.slice(); },
  get reel() { return reel?.stats() ?? null; },
  get video() { return lastVideo; },
  get highlights() { return lastHighlights ? lastHighlights.clips.map((c) => ({ type: c.primary.type, t: Math.round(c.primary.t), frames: c.frames.length, cuts: c.cuts.length, verdict: c.verdict?.label ?? null })) : null; },
  skipReel: () => reel?.skip(),
  get gl() {
    const gl = renderer.getContext();
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      renderer: ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
      memory: { ...renderer.info.memory }, calls: renderer.info.render.calls, triangles: renderer.info.render.triangles,
      pixelRatio: renderer.getPixelRatio(),
    };
  },
  session: () => session,
  get training() { return training?.state() ?? lastTraining; },
  get audio() { return { ready: audio.ready, paused: audio.paused }; },
  get coach() { return coach ? { shown: coach.shown } : null; },
  screens,
};

// ------------------------------------------------------------------ boot
audio.setMaster(currentSettings().masterVolume);
renderer.setPixelRatio(pixelRatioFor(currentSettings().quality, 'attract'));
if (autoplay) {
  screens.startQuick(auto.seed, auto.difficulty, auto.half);
} else {
  const initial = qs.get('screen') as ScreenName | null;
  const valid: ScreenName[] = ['title', 'daily', 'career', 'quick', 'howto', 'settings'];
  if (qs.get('training') === '1') {
    screens.startTraining();
  } else {
    screens.show(initial && valid.includes(initial) ? initial : 'title');
    startAttract();
  }
}
